import React from 'react';

const pageContainer: React.CSSProperties = {
  minHeight: '100vh',
  backgroundColor: '#261136',
  padding: 24,
  boxSizing: 'border-box',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-start',
  fontFamily:
    'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
};

const shellCard: React.CSSProperties = {
  width: '100%',
  maxWidth: 900,
  background:
    'radial-gradient(circle at top, #3A1F52 0, #170824 45%, #12061f 100%)',
  borderRadius: 16,
  padding: 24,
  boxSizing: 'border-box',
  boxShadow: '0 18px 45px rgba(0,0,0,0.45)',
  color: '#F5F5FA',
};

const sectionHeader: React.CSSProperties = {
  color: '#AAB5E0',
  fontSize: 20,
  marginTop: 32,
  marginBottom: 12,
};

const text: React.CSSProperties = {
  color: '#FFF',
  fontSize: 14,
  marginTop: 4,
  lineHeight: 1.6,
  opacity: 0.96,
};

const subheader: React.CSSProperties = {
  color: '#FFF',
  fontSize: 15,
  marginTop: 16,
  marginBottom: 4,
  fontWeight: 600,
};

const list: React.CSSProperties = {
  marginTop: 4,
  paddingLeft: 18,
};

const TenantTermsPage: React.FC = () => {
  return (
    <div style={pageContainer}>
      <div style={shellCard}>
        <h1 style={{ fontSize: 22, fontWeight: 600, marginBottom: 8 }}>
          Terms of Service
        </h1>
        <p style={{ ...text, opacity: 0.8 }}>Last updated: April 01, 2022</p>

        {/* Intro */}
        <p style={text}>
          These Terms of Service (&quot;Terms&quot;) govern your access to and
          use of the Bins Storage application, website, and related services
          (collectively, the &quot;Service&quot;) provided by Bins Storage, Inc.
          (&quot;Bins&quot;, &quot;we&quot;, &quot;us&quot;, or
          &quot;our&quot;). By accessing or using the Service, you agree to be
          bound by these Terms.
        </p>

        {/* You can tweak or replace everything below with your exact terms text */}

        <div style={sectionHeader}>1. Accounts</div>
        <p style={text}>
          To use certain features of the Service, you may need to create an
          account. You agree to provide accurate, current, and complete
          information during registration and to keep your account information
          up to date.
        </p>
        <p style={text}>
          You are responsible for maintaining the confidentiality of your login
          credentials and for all activities that occur under your account.
        </p>

        <div style={sectionHeader}>2. Use of the Service</div>
        <p style={text}>
          You agree to use the Service only for lawful purposes and in
          accordance with these Terms. You may not:
        </p>
        <ul style={list}>
          <li style={text}>
            Use the Service in any manner that could disable, overburden,
            damage, or impair it or interfere with any other party&apos;s use.
          </li>
          <li style={text}>
            Attempt to gain unauthorized access to any accounts, systems, or
            networks connected to the Service.
          </li>
          <li style={text}>
            Use the Service to store or transport prohibited, illegal, or
            hazardous items, as defined by applicable law and facility rules.
          </li>
        </ul>

        <div style={sectionHeader}>3. Storage Services</div>
        <p style={text}>
          Bins connects tenants, storage facilities, and drivers to facilitate
          valet and full-service storage. Storage space, pricing, and terms are
          generally set by the participating storage facility, and additional
          terms may apply.
        </p>
        <p style={text}>
          You are responsible for packing your items appropriately and for
          complying with any restrictions on what may be stored, including
          restrictions set by law or by the storage facility.
        </p>

        <div style={sectionHeader}>4. Payments and Billing</div>
        <p style={text}>
          By placing an order or storing items using the Service, you authorize
          us (or our third-party payment processor) to charge your selected
          payment method for:
        </p>
        <ul style={list}>
          <li style={text}>Storage fees (typically billed monthly).</li>
          <li style={text}>
            Pickup and delivery fees and any applicable surcharges.
          </li>
          <li style={text}>
            Any additional fees disclosed to you in the app or at checkout.
          </li>
        </ul>
        <p style={text}>
          Charges begin on the date your items are moved into storage and
          continue until your items are removed and your account is settled.
        </p>

        <div style={sectionHeader}>5. Cancellations and Changes</div>
        <p style={text}>
          You may cancel or modify an order subject to any time limits and fees
          displayed in the app or communicated by Bins or the facility.
          Generally, cancellations must be made at least 48 hours before the
          scheduled appointment time to avoid fees.
        </p>

        <div style={sectionHeader}>6. Prohibited Items</div>
        <p style={text}>
          You agree not to store items that are illegal, hazardous, perishable,
          or otherwise prohibited under applicable law or facility rules. This
          may include, without limitation:
        </p>
        <ul style={list}>
          <li style={text}>Explosives, weapons, or ammunition.</li>
          <li style={text}>Flammable or hazardous materials.</li>
          <li style={text}>Perishable food or live animals.</li>
          <li style={text}>
            Stolen property or items whose possession is unlawful.
          </li>
        </ul>

        <div style={sectionHeader}>7. Limitation of Liability</div>
        <p style={text}>
          To the maximum extent permitted by law, Bins and its affiliates,
          officers, employees, agents, and partners shall not be liable for any
          indirect, incidental, special, consequential, or punitive damages, or
          any loss of profits or revenues, arising out of or in connection with
          your use of the Service.
        </p>

        <div style={sectionHeader}>8. Indemnification</div>
        <p style={text}>
          You agree to indemnify, defend, and hold harmless Bins and its
          affiliates, officers, employees, and agents from and against any
          claims, liabilities, damages, losses, and expenses (including
          reasonable attorneys&apos; fees) arising out of or in any way
          connected with your use of the Service or violation of these Terms.
        </p>

        <div style={sectionHeader}>9. Changes to These Terms</div>
        <p style={text}>
          We may update these Terms from time to time. When we do, we will post
          the updated Terms in the app or on our website and update the &quot;Last
          updated&quot; date above. Your continued use of the Service after the
          effective date of any changes constitutes your acceptance of the
          revised Terms.
        </p>

        <div style={sectionHeader}>10. Contact Us</div>
        <p style={text}>
          If you have any questions about these Terms, you can contact us:
        </p>
        <p style={text}>By email: contact@bins-storage.com</p>
      </div>
    </div>
  );
};

export default TenantTermsPage;
